import type { Tables } from '@biopeak/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase.ts';

/**
 * Exames que o paciente enviou.
 *
 * O médico LÊ, nunca envia nem remove: o documento é do paciente, e quem
 * desfaz um envio errado é quem o fez. A RLS de `patient_documents` reflete
 * isso — o médico vinculado tem select e nada mais.
 */

export type PatientDocument = Tables<'patient_documents'>;

export function usePatientDocuments(patientId: string) {
  return useQuery({
    queryKey: ['patient-documents', patientId],
    queryFn: async (): Promise<PatientDocument[]> => {
      const { data, error } = await supabase
        .from('patient_documents')
        .select('*')
        .eq('patient_id', patientId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Abre o arquivo original numa URL assinada de vida curta.
 *
 * SIGNED URL E NÃO LINK ESTÁVEL, e o motivo é específico deste bucket: o PDF é
 * o único lugar do sistema com o dado IDENTIFICADO inteiro — nome, CPF,
 * nascimento e médico solicitante na mesma página. Tudo o mais que guardamos é
 * medida solta. Um link estável desse arquivo, colado num e-mail ou num
 * histórico de navegador, sobreviveria à revogação de acesso do médico.
 *
 * Cinco minutos: tempo de abrir e conferir, não de guardar.
 */
const TTL_SEGUNDOS = 300;

export function useOpenDocument() {
  return useMutation({
    mutationFn: async (documento: PatientDocument) => {
      const { data, error } = await supabase.storage
        .from('exames')
        .createSignedUrl(documento.storage_path, TTL_SEGUNDOS);

      if (error) throw error;
      return data.signedUrl;
    },
    onSuccess: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  });
}

/**
 * Janela do modo conferência — o exame aberto DENTRO do painel, ao lado da
 * lista de analitos.
 *
 * POR QUE NÃO SÃO OS MESMOS 5 MINUTOS DO `useOpenDocument`. Aquele teto foi
 * calibrado para "abrir e conferir": o link vai para uma aba nova, onde o
 * médico pode copiá-lo da barra de endereço, e cinco minutos limitam bem o
 * estrago de um link colado num e-mail. Aqui a tarefa é outra — validar
 * dezenas de analitos contra o documento, um a um. Um hemograma real trouxe
 * ~95 linhas; nenhum médico faz isso em cinco minutos.
 *
 * E o risco de o prazo curto não é só incômodo: o visualizador de PDF do
 * navegador busca o arquivo por RANGE REQUEST, então pular para a página 5
 * depois do vencimento falha no meio da conferência, com o médico já tendo
 * confirmado metade das linhas.
 *
 * O QUE MANTÉM O RISCO LIMITADO com o prazo maior: esta URL nunca vira link
 * clicável nem alvo de "copiar endereço" — ela existe só como `src` de um
 * iframe. Continua legível no devtools, e por isso o prazo é de trinta
 * minutos e não de horas: cobre uma sessão de conferência e vence junto com
 * ela. A propriedade que o comentário do TTL curto protege — link que
 * sobrevive à revogação de acesso — continua valendo, só que na escala da
 * tarefa real.
 */
const TTL_CONFERENCIA_SEGUNDOS = 1800;

export function useDocumentViewUrl(documento: PatientDocument | null) {
  return useQuery({
    queryKey: ['document-view-url', documento?.id],
    enabled: documento != null,
    // Vencida a URL, o iframe já carregado continua na tela — o que quebra é
    // uma navegação nova dentro do PDF. Refazer a assinatura um pouco antes do
    // fim evita isso sem remontar o visualizador no meio da leitura.
    staleTime: (TTL_CONFERENCIA_SEGUNDOS - 120) * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<string> => {
      const { data, error } = await supabase.storage
        .from('exames')
        .createSignedUrl((documento as PatientDocument).storage_path, TTL_CONFERENCIA_SEGUNDOS);

      if (error) throw error;
      return data.signedUrl;
    },
  });
}

/**
 * Marca como vistos os exames deste paciente que o médico ainda não tinha
 * aberto a aba — mesmo padrão de `useMarkChatRead`. Dispara ao montar a aba
 * Exames, não ao clicar em "Abrir exame": o aviso é sobre "chegou exame
 * novo", não sobre "você baixou o PDF".
 */
export function useMarkDocumentsViewed(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('mark_documents_viewed', {
        p_patient_id: patientId,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] });
      await queryClient.invalidateQueries({ queryKey: ['doctor-triage'] });
    },
  });
}

// ------------------------------------------------------------------ leitura

export type LabResult = Tables<'health_lab_results'>;

/** Um resultado com o nome de exibição do analito já resolvido. */
export interface LabResultComNome extends LabResult {
  analyte: { display_name: string; canonical_unit: string } | null;
}

export function useLabResults(documentId: string) {
  return useQuery({
    queryKey: ['lab-results', documentId],
    queryFn: async (): Promise<LabResultComNome[]> => {
      // Ordem do documento (`page`, depois `ordinal` — a posição do analito
      // no array da extração), não alfabética: o médico confere contra o
      // exame impresso, e reordenar por nome obrigaria ele a caçar cada
      // valor na tela em vez de simplesmente seguir de cima para baixo.
      // `nullsFirst: false` cobre linhas gravadas antes da coluna existir.
      const { data, error } = await supabase
        .from('health_lab_results')
        .select('*, analyte:analyte_catalog(display_name, canonical_unit)')
        .eq('document_id', documentId)
        .order('page', { ascending: true, nullsFirst: false })
        .order('ordinal', { ascending: true, nullsFirst: false });

      if (error) throw error;
      return (data ?? []) as unknown as LabResultComNome[];
    },
  });
}

/**
 * Dispara a leitura automática do documento.
 *
 * A edge function é quem confere consentimento, mascara e chama o modelo. O
 * painel não decide nada disso — só pede.
 */
export function useExtractDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      const { data, error } = await supabase.functions.invoke('document-extract', {
        body: { documentId },
      });

      // A função devolve 200 com `status: 'sem_texto'` quando o arquivo é foto
      // ou digitalização: não é erro, é um resultado legítimo que a interface
      // precisa saber explicar.
      if (error) {
        const detalhe = await (error as { context?: Response }).context
          ?.json()
          .catch(() => null);
        throw new Error(
          detalhe?.message ?? detalhe?.error ?? 'Não foi possível ler o documento.',
        );
      }
      return data as { status: string; message?: string; resumo?: Record<string, unknown> };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['patient-documents'] });
      await queryClient.invalidateQueries({ queryKey: ['lab-results'] });
    },
  });
}

/**
 * O "fiscal": duas leituras independentes a mais do mesmo documento,
 * comparadas analito a analito contra o que já está gravado.
 *
 * Não sobrescreve nada — só chama a edge function que grava as leituras
 * novas e recalcula `ai_agreement`/`ai_agreement_total` em cada linha.
 */
export function useRevalidateDocument(documentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('document-revalidate', {
        body: { documentId },
      });
      if (error) {
        const detalhe = await (error as { context?: Response }).context
          ?.json()
          .catch(() => null);
        throw new Error(
          detalhe?.message ?? detalhe?.error ?? 'Não foi possível revalidar agora.',
        );
      }
      return data as { status: string; message?: string; leituras_gravadas?: number };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['lab-results', documentId] });
    },
  });
}

// ------------------------------------------------------ achados do hemograma

export type HemogramFinding = Tables<'hemogram_pattern_findings'>;

/**
 * Achados que a função `private.evaluate_hemogram_patterns` recalcula toda
 * vez que o médico confirma/corrige um resultado (ver `review_document`).
 * É a foto mais recente, não histórico — por isso não tem paginação nem
 * ordenação por data, só por severidade.
 */
export function useHemogramFindings(patientId: string) {
  return useQuery({
    queryKey: ['hemogram-findings', patientId],
    queryFn: async (): Promise<HemogramFinding[]> => {
      const { data, error } = await supabase
        .from('hemogram_pattern_findings')
        .select('*')
        .eq('patient_id', patientId);

      if (error) throw error;
      const ordem: Record<string, number> = { alerta: 0, atencao: 1, info: 2 };
      return (data ?? []).sort(
        (a, b) => (ordem[a.severity] ?? 9) - (ordem[b.severity] ?? 9),
      );
    },
  });
}

export type Acao = 'confirmar' | 'corrigir' | 'rejeitar';

export interface Decisao {
  id: string;
  acao: Acao;
  /** Só em `corrigir`. String porque vem de input. */
  valor?: string;
}

/**
 * O PORTÃO. Só através daqui um valor lido por modelo passa a valer.
 *
 * Chama a RPC `review_document`, que é SECURITY DEFINER e grava a auditoria
 * junto — as tabelas não têm policy de UPDATE para `authenticated`, então não
 * existe caminho alternativo.
 */
export function useReviewDocument(documentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (decisoes: Decisao[]) => {
      const { data, error } = await supabase.rpc('review_document', {
        p_document_id: documentId,
        // O tipo gerado do parâmetro jsonb é `Json`, que exige index signature.
        // `Decisao` é uma interface fechada de propósito — ela documenta o
        // contrato da RPC, e afrouxá-la para `Record<string, unknown>` só para
        // agradar o compilador perderia isso. O cast fica na fronteira.
        p_decisoes: decisoes as unknown as never,
      });
      if (error) throw error;
      return data as Record<string, number>;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['lab-results', documentId] });
      await queryClient.invalidateQueries({ queryKey: ['patient-documents'] });
      // A tendência muda no instante em que um valor é confirmado.
      await queryClient.invalidateQueries({ queryKey: ['patient-trend'] });
    },
  });
}
